import { useCallback, useState } from "react";

import type { Question } from "@frontierx/shared/schemas/Question";
import { QuestionSchema } from "@frontierx/shared/schemas/Question";
import { Sparkles } from "lucide-react";
import useLocalStorageState from "use-local-storage-state";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

import { usePartySocketWithReconnect } from "../../hooks/usePartySocketWithReconnect";
import { useCallContext } from "../Call/CallContext";
import {
  type ParticipantRoleFilter,
  ParticipantRoleFilterDropdown,
} from "../Filter/ParticipantRoleFilterDropdown";
import { PanelHeader } from "../PanelHeader";
import { QuestionsList } from "./QuestionsList";

function QuestionDisplayInner() {
  const {
    aiHistory,
    handleGetHelp,
    handleGetAIHelp,
    isStreaming,
    roomBot,
    roomId,
    setActiveMode,
    participantRoles,
  } = useCallContext();
  const [displayFilter, setDisplayFilter] =
    useLocalStorageState<ParticipantRoleFilter>("question-display-filter", {
      defaultValue: "customer",
    });
  const [callQuestions, setCallQuestions] = useState<Question[]>([]);
  const { socket: questionSocket } = usePartySocketWithReconnect({
    room: roomId,
    party: "questions",
    onMessage(event) {
      const data = JSON.parse(event.data);
      console.log("Questions socket received message:", data);
      if (data.type === "QUESTIONS") {
        setCallQuestions(
          data.payload.sort((a: Question, b: Question) =>
            b.id.localeCompare(a.id),
          ),
        );
      }
      if (data.type === "QUESTION") {
        const newQuestion = QuestionSchema.parse(data.payload);
        setCallQuestions((prevQuestions) => {
          const existingIndex = prevQuestions.findIndex(
            (q) => q.id === newQuestion.id,
          );
          let updatedQuestions: Question[];
          if (existingIndex !== -1) {
            // Replace existing question
            updatedQuestions = [
              ...prevQuestions.slice(0, existingIndex),
              newQuestion,
              ...prevQuestions.slice(existingIndex + 1),
            ];
          } else {
            // Append new question
            updatedQuestions = [...prevQuestions, newQuestion];
          }
          return updatedQuestions.sort((a, b) => b.id.localeCompare(a.id));
        });
      }
    },
  });

  const handleAnswerQuestion = useCallback(
    (questionId: string) => {
      questionSocket.send(
        JSON.stringify({
          type: "ANSWER_QUESTION",
          payload: { questionId },
        }),
      );
    },
    [questionSocket],
  );

  // Helper function to get participant role for questions
  const getParticipantRole = (
    speakerId: number,
  ): "salesperson" | "customer" | "observer" | "unknown" => {
    const participantInfo = participantRoles?.participants.find(
      (p) => p.id === speakerId,
    );
    if (participantInfo) {
      return participantInfo.role === "client"
        ? "customer"
        : participantInfo.role;
    }

    // Fallback to host/guest logic for unassigned participants
    const speaker = roomBot?.meeting_participants?.find(
      (p) => p.id === speakerId,
    );
    if (!speaker) {
      return "unknown";
    }

    return speaker.is_host ? "salesperson" : "customer";
  };

  const filteredQuestions = callQuestions.filter((q) => {
    if (displayFilter === "all") {
      return true;
    }

    const participantRole = getParticipantRole(q.speaker_id);
    return participantRole === displayFilter;
  });

  const displayedQuestions = filteredQuestions;
  return (
    <div className="flex h-full flex-col">
      <PanelHeader title="Questions">
        <div className="flex w-full flex-row space-x-2">
          <div className="flex-grow-0">
            <ParticipantRoleFilterDropdown
              value={displayFilter}
              onSelect={setDisplayFilter}
            />
          </div>
          <div className="flex-grow-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                handleGetHelp({});
                setActiveMode("ai");
              }}
              disabled={isStreaming}
            >
              <Sparkles size={16} className="mr-2" />
              {isStreaming ? "Getting AI Help..." : "Get AI Help"}
            </Button>
          </div>
        </div>
      </PanelHeader>
      <div className="flex flex-grow flex-col overflow-hidden">
        <ScrollArea className="flex-grow">
          <QuestionsList
            onGetAIHelp={handleGetAIHelp}
            questions={displayedQuestions}
            onAnswerQuestion={handleAnswerQuestion}
            aiHistory={aiHistory}
          />
        </ScrollArea>
      </div>
    </div>
  );
}

export function QuestionDisplay() {
  const { roomId } = useCallContext();
  const { reconnectAttempt } = usePartySocketWithReconnect({
    room: roomId,
    party: "questions",
    onMessage: () => {}, // No-op since we only need the reconnectAttempt
  });

  return <QuestionDisplayInner key={reconnectAttempt} />;
}
